import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { CreateCompanyCrewMemberDto, CompanyCrewMemberDto, UpdateCompanyCrewMemberDto } from '../dtos/company-crew-member.dto';
import { createCrewMember, updateCrewMember } from '../services/crew-member.service';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

interface CrewMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onCrewMemberAdded: () => void;
  crewMemberToEdit?: CompanyCrewMemberDto;
}

export const CrewMemberDialog = ({ open, onClose, onCrewMemberAdded, crewMemberToEdit }: CrewMemberDialogProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Partial<CreateCompanyCrewMemberDto>>({
    name: '',
    role: 'Groundsman',
    hourlyRate: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadFormDataFromCrewMember = useCallback(() => {
    if (crewMemberToEdit) {
      setFormData({
        name: crewMemberToEdit.name,
        role: crewMemberToEdit.role,
        hourlyRate: crewMemberToEdit.hourlyRate ?? undefined
      });
    } else {
      setFormData({
        name: '',
        role: 'Groundsman',
        hourlyRate: undefined
      });
    }
  }, [crewMemberToEdit, setFormData]);

  useEffect(() => {
    loadFormDataFromCrewMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    loadFormDataFromCrewMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewMemberToEdit]);

  const validateFormData = (formData: Partial<CreateCompanyCrewMemberDto>): CreateCompanyCrewMemberDto | undefined => {
    if (!formData.name || !formData.role) {
      return undefined;
    }
    // SQ-522: a person's billable rate lives on their team profile; the crew
    // list carries none.
    return {
      name: formData.name,
      role: formData.role,
    };
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (crewMemberToEdit) {
        const updateDto: UpdateCompanyCrewMemberDto = {
          name: formData.name,
          role: formData.role,
          hourlyRate: formData.hourlyRate
        };
        await updateCrewMember(navigate, crewMemberToEdit.id, updateDto);
      } else {
        const validatedFormData = validateFormData(formData);
        if (!validatedFormData) {
          enqueueSnackbar('Please fill in all fields', { variant: 'error' });
          return;
        }
        await createCrewMember(navigate, validatedFormData);
      }
      onCrewMemberAdded();
      onClose();
    } catch (error) {
      console.error('Failed to save crew member:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{crewMemberToEdit ? 'Edit Crew Member' : 'Add New Crew Member'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Role</InputLabel>
          <Select
            value={formData.role}
            label="Role"
            onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
            required
          >
            <MenuItem value="Climber">Climber</MenuItem>
            <MenuItem value="Groundsman">Groundsman</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isSubmitting || !formData.name || !formData.role}
        >
          {crewMemberToEdit ? 'Save Changes' : 'Add Crew Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 